/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  AgendaAppointmentItemDataFragment,
  HealthProfessionalItemDataFragment,
} from '../../types/api/graphql';
import { useHealthProviderItems } from '../health-providers/use-health-provider-items';
import { useHealthProfessionalAppointmentsCheck } from './use-health-professional-appointments-check';

enum ToolTipReason {
  CURRENT = 'current',
  ADMIN = 'admin',
  AGENDA = 'agenda',
}

type HealthProfessionalAgenda = {
  active: boolean;
  healthProviderId: string;
  practiceActive: boolean;
  patientWeeksBefore: number;
};

const useHealthProfessionalHealthProviderAgendas = (
  healthProfessionalData: HealthProfessionalItemDataFragment | null | undefined,
  healthProfessionalDataLoading: boolean,
  healthProviderId: string | null | undefined
) => {
  const intl = useIntl();

  const [healthProfessionalAgendas, setHealthProfessionalAgendas] = useState<
    HealthProfessionalAgenda[]
  >([]);

  useEffect(() => {
    if (healthProfessionalData && !healthProfessionalDataLoading) {
      setHealthProfessionalAgendas(healthProfessionalData.agendas ?? []);
    }
  }, [healthProfessionalData, healthProfessionalDataLoading]);

  // get healthproviders
  const {
    data: healthProviderItemsData,
    loading: healthProviderItemsDataLoading,
  } = useHealthProviderItems();

  // get appointments
  const {
    data: healthProfessionalAppointmentsCheckData,
    loading: healthProfessionalAppointmentsCheckLoading,
  } = useHealthProfessionalAppointmentsCheck(healthProfessionalData?.id);

  const loading = useMemo(
    () =>
      healthProviderItemsDataLoading ||
      healthProfessionalDataLoading ||
      healthProfessionalAppointmentsCheckLoading,
    [
      healthProfessionalAppointmentsCheckLoading,
      healthProfessionalDataLoading,
      healthProviderItemsDataLoading,
    ]
  );

  const update = useCallback((id: string, value: string) => {
    console.log('setPractice', id, value);
    setHealthProfessionalAgendas(prevState => {
      const newState = [...prevState];
      const index = newState.findIndex(
        option => option.healthProviderId === id
      );
      const weeksBefore = parseInt(value);
      if (index !== -1) {
        newState.splice(index, 1);
      }
      newState.push({
        healthProviderId: id,
        active: weeksBefore > -2,
        patientWeeksBefore: weeksBefore,
        practiceActive: weeksBefore !== -3,
      });
      return newState;
    });
  }, []);

  const practices = useMemo(() => {
    let result: {
      id: string;
      name: string;
      reasons: string[];
      value: number;
      appointments: AgendaAppointmentItemDataFragment[];
      options: { key: string; title: string; disabled: boolean }[];
    }[] = [];

    if (healthProviderItemsData && healthProfessionalAppointmentsCheckData) {
      result = healthProviderItemsData.map(practice => {
        console.log('practice', practice);
        // get appointments
        const appointments =
          healthProfessionalAppointmentsCheckData.agendas.filter(
            x => x.healthProviderId === practice.id
          )?.[0]?.appointments ?? [];

        // check if current health provider
        const isCurrentHealthProvider = practice.id === healthProviderId;

        // reasons
        const reasons: string[] = [];
        if (isCurrentHealthProvider) {
          reasons.push(
            intl.formatMessage({
              id: `health-professional.agenda.update.error.${ToolTipReason.CURRENT}`,
            })
          );
        }

        if (appointments.length > 0) {
          reasons.push(
            intl.formatMessage({
              id: `health-professional.agenda.update.error.${ToolTipReason.AGENDA}`,
            })
          );
        }

        const getTime = (value: string | null | undefined) => {
          if (value) {
            return intl.formatDate(value, {
              hour: 'numeric',
              minute: 'numeric',
            });
          }

          return null;
        };

        const appointmentMessage = appointments
          .map(appointment => {
            if (appointment.startTime) {
              return intl.formatMessage(
                {
                  id: 'common.appointment-date',
                },
                {
                  date: intl.formatDate(appointment.startTime, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  }),
                  time: [
                    getTime(appointment.startTime),
                    getTime(appointment.endTime),
                  ]
                    .filter(option => option)
                    .join(' - '),
                }
              );
            }

            return null;
          })
          .filter(a => a !== null);

        // todo refactor
        appointmentMessage.forEach(appointment => {
          reasons.push(appointment);
        });

        console.log('agendas ??? ', healthProfessionalAgendas);

        // get value of agenda
        const healthProfessionalAgenda = healthProfessionalAgendas.find(
          x => x.healthProviderId === practice.id
        );

        console.log('Agenda', healthProfessionalAgenda);

        // practice not active = -3 (off)
        // practice active = -2 (disabled) or patientWeeksBefore
        let value = isCurrentHealthProvider ? -2 : -3;
        console.log(practice.name, 'before', value);
        if (healthProfessionalAgenda?.practiceActive) {
          value = healthProfessionalAgenda?.active
            ? healthProfessionalAgenda?.patientWeeksBefore ?? -2
            : -2;
        }
        console.log(practice.name, 'after', value);

        const options = [
          {
            key: '-3',
            title: 'health-professional.agendas.off',
            disabled: isCurrentHealthProvider || appointments.length > 0,
          },
          {
            key: '-2',
            title: 'health-professional.agendas.disabled',
            disabled: appointments.length > 0,
          },
        ];

        return {
          id: practice.id,
          name: practice.name,
          reasons,
          value,
          appointments,
          options,
        };
      });
    }
    console.log('HealthProfessionalHealthProviderAgendas practices', result);
    return result;
  }, [
    healthProfessionalAgendas,
    healthProfessionalAppointmentsCheckData,
    healthProviderId,
    healthProviderItemsData,
    intl,
  ]);

  return {
    loading,
    practices,
    update,
  };
};
export { useHealthProfessionalHealthProviderAgendas };
